const titleCase = (value) =>
  String(value || '')
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Resolve a batch's branch reference to a consistent shape.
 *
 * Accepts either a raw slug string (legacy) or a Firebase push id, or a batch
 * object with `branchId`/`branchName`/`branch` fields. Falls back to a
 * title-cased version of the slug when no settings entry exists yet.
 */
export const resolveBranchRef = (value, { byId = {}, bySlug = {} } = {}) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Batch object with branch fields
  if (typeof value === 'object') {
    const { branchId, branch, branchName } = value;
    if (branchId && byId[branchId]) {
      const resolved = byId[branchId];
      return {
        id: resolved.id,
        name: resolved.name || branchName || titleCase(branch),
        slug: resolved.slug || slugify(resolved.name || branch),
        isActive: resolved.isActive !== false,
        legacy: false,
      };
    }
    if (branchId) {
      return {
        id: branchId,
        name: branchName || titleCase(branch) || 'Branch',
        slug: slugify(branch) || null,
        isActive: true,
        legacy: true,
      };
    }
    if (branch && bySlug[branch]) {
      const resolved = bySlug[branch];
      return {
        id: resolved.id,
        name: resolved.name,
        slug: resolved.slug,
        isActive: resolved.isActive !== false,
        legacy: false,
      };
    }
    if (branch) {
      return {
        id: null,
        name: branchName || titleCase(branch),
        slug: slugify(branch),
        isActive: true,
        legacy: true,
      };
    }
    return null;
  }

  // String: could be id or slug
  const raw = String(value).trim();
  if (byId[raw]) {
    const resolved = byId[raw];
    return {
      id: resolved.id,
      name: resolved.name,
      slug: resolved.slug,
      isActive: resolved.isActive !== false,
      legacy: false,
    };
  }
  const slug = slugify(raw);
  if (bySlug[slug]) {
    const resolved = bySlug[slug];
    return {
      id: resolved.id,
      name: resolved.name,
      slug: resolved.slug,
      isActive: resolved.isActive !== false,
      legacy: false,
    };
  }
  return {
    id: null,
    name: titleCase(raw),
    slug,
    isActive: true,
    legacy: true,
  };
};

export const resolveBranchName = (value, catalogs) => {
  const ref = resolveBranchRef(value, catalogs);
  return ref?.name || '';
};

export default resolveBranchRef;
